//import Environment from '../../constants/Environment';
import initialTogglesDictionary from '../../featureToggle/initialTogglesDictionary';

/* eslint-disable no-console */
// OIDC Configuration from environment variables
const OIDC_CONFIG = {
  name: null,
  description: null,
  provider: null,
  wsUuid: null,
  providerUrl: null, // Will be fetched dynamically
  providerId:
    // eslint-disable-next-line prettier/prettier
    initialTogglesDictionary.OidcProviderId || "flexeserve-management",
  clientId: null, // process.env.REACT_APP_OIDC_CLIENT_ID,
  redirectUri:
    process.env.REACT_APP_OIDC_REDIRECT_URI ||
    window.location.origin + '/#/callback',
  backendUrl: process.env.REACT_APP_BACKEND_API_AUTH || 'api/auth',
  scope: null, // Will be fetched from authinfo API
};

// Validate configuration (providerUrl will be validated after fetching)
if (
  !OIDC_CONFIG.providerId ||
  !OIDC_CONFIG.redirectUri ||
  !OIDC_CONFIG.backendUrl
) {
  console.error('Missing required environment variables');
}

// Fetch auth URL dynamically from backend
export async function initializeAuthConfig() {
  try {
    const url = `${OIDC_CONFIG.backendUrl}/idp/${OIDC_CONFIG.providerId}/config`;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch auth config: ${response.status}`);
    }

    const data = await response.json();
    if (!data.oidc_auth_url) {
      console.log('OIDC authentication URL not provided');
      throw new Error('No auth_url in response');
    }

    OIDC_CONFIG.clientId = data.oidc_client_id
      ? data.oidc_client_id
      : OIDC_CONFIG.providerId;
    OIDC_CONFIG.providerUrl = data.oidc_auth_url;
    OIDC_CONFIG.name = data.name || null;
    OIDC_CONFIG.description = data.description || null;
    OIDC_CONFIG.provider = data.provider || null;
    OIDC_CONFIG.wsUuid = data.ws_uuid || null;
    if (data.oidc_scope) {
      OIDC_CONFIG.scope = data.oidc_scope;
    }
    console.log('Auth configuration loaded successfully');
    return { success: true, OIDC_CONFIG };
  } catch (error) {
    console.error('Failed to initialize auth configuration:', error);
    return { success: false, error: error.message };
  }
}

// Initiate OIDC login flow with PKCE
export async function initiateLogin() {
  const state = generateRandomString(32);
  const nonce = generateRandomString(32);
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Store for verification
  sessionStorage.setItem('oidc_state', state);
  sessionStorage.setItem('oidc_nonce', nonce);
  sessionStorage.setItem('oidc_code_verifier', codeVerifier);

  // auth_url from backend is already the complete authorization endpoint
  const authorizationEndpoint = OIDC_CONFIG.providerUrl;

  const params = new URLSearchParams({
    client_id: OIDC_CONFIG.clientId,
    redirect_uri: OIDC_CONFIG.redirectUri,
    response_type: 'code',
    scope: OIDC_CONFIG.scope,
    state: state,
    nonce: nonce,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    prompt: 'login',
  });

  // Store scope for later use during token exchange
  sessionStorage.setItem('oidc_scope', OIDC_CONFIG.scope);

  window.location.href = `${authorizationEndpoint}?${params.toString()}`;
}

// Exchange authorization code for token via backend (with PKCE)
export async function exchangeCodeForToken(code) {
  try {
    const codeVerifier = sessionStorage.getItem('oidc_code_verifier');
    const state = sessionStorage.getItem('oidc_state');
    const scope = sessionStorage.getItem('oidc_scope');
    const nonce = sessionStorage.getItem('oidc_nonce');

    if (!state || !codeVerifier || !scope) {
      throw new Error('Code verifier not found - session may have expired');
    }

    // Verify state parameter
    const params = new URLSearchParams(window.location.search);
    const returnedState = params.get('state');

    if (returnedState !== state) {
      console.log(
        'returned state: "',
        returnedState,
        '"; expected state: "',
        state,
        '"'
      );
      throw new Error('State parameter mismatch - possible CSRF attack');
    }

    // Clean up session storage
    sessionStorage.removeItem('oidc_nonce');
    sessionStorage.removeItem('oidc_scope');
    sessionStorage.removeItem('oidc_state');
    sessionStorage.removeItem('oidc_code_verifier');

    // Call backend to exchange code for token
    const response = await fetch(
      `${OIDC_CONFIG.backendUrl}/idp/${OIDC_CONFIG.providerId}/token`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'authorization_code',
          provider_id: OIDC_CONFIG.providerId,
          code: code,
          nonce: nonce,
          scope: scope,
          client_id: OIDC_CONFIG.clientId,
          redirect_uri: OIDC_CONFIG.redirectUri,
          code_verifier: codeVerifier,
        }),
      }
    );

    const data = await response.json();
    if (response.ok) {
      console.log(`token exchange completed; CODE=${response.status}`);
      return {
        success: true,
        data: data,
      };
    }
    console.log(`token exchange failed; CODE=${response.status}; MSG=${data}`);
    return {
      success: false,
      error: data.error_description || data.message || 'Token exchange failed',
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

// Refresh access token using refresh token
export async function refreshAccessToken() {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return {
        success: false,
        error: 'No refresh token available',
      };
    }
    const tokenEndpoint = `${OIDC_CONFIG.providerUrl}/protocol/openid-connect/token`;

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: OIDC_CONFIG.clientId,
      }).toString(),
    });
    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        error:
          error.error_description || error.message || 'Token refresh failed',
      };
    }
    // Update tokens in localStorage
    const data = await response.json();
    localStorage.setItem('access_token', data.access_token);
    console.log(`A-JWT: ${data.access_token}`);
    if (data.id_token) {
      console.log(`I-JWT: ${data.id_token}`);
      localStorage.setItem('id_token', data.id_token);
    }
    if (data.refresh_token) {
      console.log(`R-JWT: ${data.refresh_token}`);
      localStorage.setItem('refresh_token', data.refresh_token);
      // Store refresh token expiry time from the response
      if (data.expires_in) {
        const refreshTokenExpiresAt = Date.now() + data.expires_in * 1000;
        localStorage.setItem(
          'refresh_token_expires_at',
          refreshTokenExpiresAt.toString()
        );
      }
    }

    return {
      success: true,
      data: {
        access_token: data.access_token,
        id_token: data.id_token,
        token_type: data.token_type,
        expires_in: data.expires_in,
      },
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
}

// Check if token is expired
export function isTokenExpired(token) {
  try {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
      return true;
    }
    // Check if token expires within next 60 seconds
    return decoded.exp * 1000 < Date.now() + 60000;
  } catch (error) {
    return true;
  }
}

// Schedule refresh token renewal at 66% of its lifetime
export function scheduleRefreshTokenRenewal(onRefreshComplete) {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return null;
    }

    const decoded = decodeJWT(refreshToken);
    let delayUntilRenewal = null;

    // Try to use JWT claims if available
    if (decoded && decoded.exp && decoded.iat) {
      const issuedAt = decoded.iat * 1000; // Convert to ms
      const expiresAt = decoded.exp * 1000; // Convert to ms
      const lifetime = expiresAt - issuedAt;
      const renewalPoint = issuedAt + lifetime * 0.66; // 66% of lifetime
      delayUntilRenewal = renewalPoint - Date.now();
    } else {
      // Fall back to stored expiry time from code exchange
      const expiresAtStr = localStorage.getItem('refresh_token_expires_at');
      if (expiresAtStr) {
        const expiresAt = parseInt(expiresAtStr, 10);
        const lifetime = expiresAt - (Date.now() - (expiresAt - Date.now())); // Approximate lifetime
        const issuedAt = expiresAt - lifetime;
        const renewalPoint = issuedAt + lifetime * 0.66;
        delayUntilRenewal = renewalPoint - Date.now();
      }
    }
    if (!delayUntilRenewal || delayUntilRenewal <= 0) {
      return null;
    }
    //console.log(
    //  `Refresh token will be renewed in ${Math.round(
    //    delayUntilRenewal / 1000
    //  )} seconds (at 66% of lifetime)`
    //);
    const timeoutId = setTimeout(async () => {
      //console.log('Renewing refresh token...');
      const result = await refreshAccessToken();
      if (result.success) {
        //console.log('Refresh token renewed successfully');
        // Schedule the next renewal
        if (onRefreshComplete) {
          onRefreshComplete();
        }
      } else {
        console.error('Failed to renew refresh token:', result.error);
      }
    }, delayUntilRenewal);

    return timeoutId;
  } catch (error) {
    console.error('Error scheduling refresh token renewal:', error);
    return null;
  }
}

// Call backend API with access token
export async function callBackendApi(
  endpoint,
  accessToken,
  method = 'GET',
  body = null
) {
  try {
    const startTime = performance.now();
    console.log(`CB-JWT: ${accessToken}`);
    const options = {
      method: method,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    };
    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(
      `${OIDC_CONFIG.backendUrl}${endpoint}`,
      options
    );
    const endTime = performance.now();
    const duration = Math.round((endTime - startTime) * 100) / 100;

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: 'API error' }));
      return {
        success: false,
        error: error.message || 'API call failed',
        status: response.status,
        duration: duration,
      };
    }
    const data = await response.json();
    return {
      success: true,
      data: data,
      duration: duration,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      duration: 0,
    };
  }
}

// Generate random string for state/nonce
function generateRandomString(length) {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Generate PKCE code verifier (RFC 7636)
function generateCodeVerifier() {
  const length = 128;
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Generate PKCE code challenge from verifier (RFC 7636)
async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await crypto.subtle.digest('SHA-256', data);

  // Base64url encode
  const hashArray = Array.from(new Uint8Array(hash));
  const hashString = String.fromCharCode.apply(null, hashArray);
  const base64 = btoa(hashString);

  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Decode JWT (ID token)
function decodeJWT(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    const decoded = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

export { OIDC_CONFIG };
